import { SizableText, XStack, YStack, YStackProps } from '@mythical/ui'
import { RenderBlocks } from 'app/components/blocks/RenderBlocks'
import { CollectionList } from 'app/components/collection/CollectionList'
import { Button, ButtonLink } from 'app/components/primitive/Button'
import Spinner from 'app/components/primitive/Spinner'
import { Container } from 'app/components/structure/Container'
import MainLayout from 'app/components/structure/Layout/MainLayout'
import useAllSeriesPage from 'app/hooks/use-all-series-page'
import { useState } from 'react'
import { PromoBanner as IPromoBannerBlock } from 'app/blocks/promo-banner'
import { AllSeriesPageContent } from 'app/queries/globals/all-series-page'
import { useQueryRefresh } from 'app/hooks/use-query-refresh'
import { RefreshControl } from 'app/components/primitive/RefreshControl'

const tabs = [
  { label: 'For You', value: 'for-you' },
  { label: 'All Series', value: 'all' },
] as const

export function CollectionListScreen() {
  const [tab, setTab] = useState<(typeof tabs)[number]['value']>('for-you')
  const { data, loading, error, fetchData } = useAllSeriesPage()

  const promoBannerBlock = data?.pageContent?.find(
    (block) => block.__typename === 'PromoBanner',
  ) as IPromoBannerBlock

  const { refresh, refreshing } = useQueryRefresh(fetchData)

  return (
    <MainLayout
      promoBannerProps={promoBannerBlock}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={refresh} />
      }
    >
      <Container paddedX variant="none" pb="$5" ai="center">
        <XStack boc="$borderSecondary" bw="$1" br="$pill" p="$1.5" gap="$1">
          {tabs.map((t) => (
            <Button
              key={t.value}
              theme={t.value === tab ? 'white' : 'transparent'}
              onPress={() => setTab(t.value)}
              size="$3"
              miw={100}
              h={32}
              $gtSm={{ miw: 140 }}
            >
              {t.label}
            </Button>
          ))}
        </XStack>
      </Container>

      {tab === 'all' && (
        <Container
          fs={1}
          fg={1}
          pb="$4"
          paddedX
          variant="none"
          gap="$5"
          ai="center"
        >
          <CollectionList />
        </Container>
      )}

      {tab === 'for-you' && (
        <Container fs={1} fg={1} variant="none" gap="$5" ai="center">
          <ForYouBlocks data={data} loading={loading} error={error} />
        </Container>
      )}
    </MainLayout>
  )
}

interface ForYouBlocksProps {
  data: AllSeriesPageContent | undefined
  loading: boolean
  error: any
  wrapperProps?: YStackProps
}

const ForYouBlocks = ({
  data,
  loading,
  error,
  wrapperProps,
}: ForYouBlocksProps) => {
  const { pageContent } = data || {}
  return (
    <YStack w="100%" fs={1} fg={1} gap="$5" {...wrapperProps}>
      <RenderBlocks spaced blocks={pageContent || []} />

      {loading && (
        <YStack fs={1} fg={1} jc="center" ai="center">
          <Spinner size="large" />
        </YStack>
      )}

      {!!error && !loading && (
        <YStack fs={1} fg={1} jc="center" ai="center" gap="$3.5">
          <SizableText
            ff="$heading"
            size="$s"
            $gtSm={{ size: '$m' }}
            color="$fgPrimary"
            ta="center"
            maw={700}
            mx="auto"
          >
            Oops! something's wrong here
          </SizableText>
          <ButtonLink href="/">Reload Homepage</ButtonLink>
        </YStack>
      )}
    </YStack>
  )
}
